import React from 'react';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { Outlet } from 'react-router-dom';
import { AppRoute } from '../routes.types';
import { USER_ROLES } from '../../utils/constants';
import { VenueShell } from '../shells/VenueShell';
import { VenueId } from '../../venues/venues.types';

const AdminMarketplace = lazyWithRetry(() => import('../../containers/AdminMarketplace/AdminMarketplace'));
const TimelineEditor = lazyWithRetry(() => import('../../containers/AdminMarketplace/TimelinePage/TimelineEditor'));
const LandscapeMapDemoPage = lazyWithRetry(() => import('../../containers/LandscapeMapContainer/LandscapeMapDemo'));
const MarketplaceTemplatePreview = lazyWithRetry(() => import('../../containers/AdminMarketplace/MarketplaceTemplatePreview/MarketplaceTemplatePreview'));
const ConnectMarketplaceDashboard = lazyWithRetry(() => import('../../containers/ConnectMarketplaceDashboard/ConnectMarketplaceDashboard'));
const DataAndDocumentTemplatesDashboard = lazyWithRetry(() =>
    import('../../containers/Admin/DataRequest/DataAndDocumentTemplatesDashboard/DataAndDocumentTemplatesDashboard')
        .then((m) => ({ default: m.DataAndDocumentTemplatesDashboard }))
);
const DataAndDocumentRequestsDashboard = lazyWithRetry(() =>
    import('../../containers/Admin/DataRequest/DataAndDocumentRequestsDashboard/DataAndDocumentRequestsDashboard')
        .then((m) => ({ default: m.DataAndDocumentRequestsDashboard }))
);
const ProjectEditorHost = lazyWithRetry(() =>
    import('../../containers/Admin/DataRequest/ProjectEditorHost/ProjectEditorHost')
        .then((m) => ({ default: m.ProjectEditorHost }))
);
const Controls = lazyWithRetry(() => import('../../containers/Admin/Controls/Controls'));
const ChartImage = lazyWithRetry(() => import('../../containers/Admin/chart-image'));
const PricingComponent = lazyWithRetry(() => import('../../containers/Admin/Pricing/PricingComponent'));
const PaidReportFromUpdates = lazyWithRetry(() => import('../../containers/Admin/PaidReportFromUpdates'));
const KnowledgeBaseContentManager = lazyWithRetry(() => import('../../containers/KnowledgeBase/KnowledgeBaseContentManager/KnowledgeBaseContentManager'));
const SupportPortalPage = lazyWithRetry(() => import('../../containers/SupportRequestsPage/SupportRequestsPage'));
const AdminNotificationSettingsPage = lazyWithRetry(() => import('../../containers/AdminNotificationSettingsPage/AdminNotificationSettingsPage'));
const LegalDocumentsManager = lazyWithRetry(() =>
    import('../../containers/Admin/LegalDocuments/LegalDocumentsManager')
        .then((m) => ({ default: m.LegalDocumentsManager }))
);
const DocumentExtractionPage = lazyWithRetry(() =>
    import('../../containers/Admin/DocumentExtraction/DocumentExtractionPage')
);
const PracticeManagementGroupsAdmin = lazyWithRetry(() => import('../../containers/PracticeManagementGroups/PracticeManagementAdmin'));
const CorporateGroupManagementAdmin = lazyWithRetry(() => import('../../containers/CorporateGroupManagement/CorporateGroupManagementAdmin'));
const MaintenanceTool = lazyWithRetry(() =>
    import('../../containers/Maintenance/MaintenanceTool').then((m) => ({ default: m.MaintenanceTool }))
);
const TimelineSimulator = lazyWithRetry(() =>
    import('../../containers/Maintenance/TimelineSimulator').then((m) => ({ default: m.TimelineSimulator }))
);
const ClientErrorReports = lazyWithRetry(() =>
    import('../../containers/Maintenance/ClientErrorReports').then((m) => ({ default: m.ClientErrorReports }))
);
const QuartzMonitor = lazyWithRetry(() =>
    import('../../containers/Maintenance/QuartzMonitor/QuartzMonitor').then((m) => ({ default: m.QuartzMonitor }))
);
const PracticeList = lazyWithRetry(() => import('../../containers/Practices/Admin/PracticeList'));
const PublicFreemiumReportsPage = lazyWithRetry(() =>
    import('../../containers/Admin/PublicFreemiumReports/PublicFreemiumReportsPage')
        .then((m) => ({ default: m.PublicFreemiumReportsPage }))
);
const ViewAsBuyerPage = lazyWithRetry(() =>
    import('../../containers/Admin/ViewAsBuyer/ViewAsBuyerPage')
        .then((m) => ({ default: m.ViewAsBuyerPage }))
);

export const ADMIN_ROUTES: AppRoute[] = [
    {
        path: '/admin',
        element: <Outlet />,
        roles: [USER_ROLES.ADMIN],
        withAppShell: true,
        children: [
            { path: 'controls', element: <Controls /> },
            { path: 'upload-chart', element: <ChartImage /> },
            { path: 'pricing', element: <PricingComponent /> },
            { path: 'paidreport-from-updates', element: <PaidReportFromUpdates /> },
            { path: 'knowledge-library', element: <KnowledgeBaseContentManager /> },
            { path: 'support-requests', element: <SupportPortalPage /> },
            { path: 'notifications', element: <AdminNotificationSettingsPage /> },
            { path: 'legal-documents', element: <LegalDocumentsManager /> },
            { path: 'document-extraction', element: <DocumentExtractionPage /> },
            { path: 'public-fe-requests', element: <PublicFreemiumReportsPage /> },
        ],
    },
    { path: '/pmg-admin', element: <PracticeManagementGroupsAdmin />, roles: [USER_ROLES.ADMIN] },
    { path: '/cgm-admin', element: <CorporateGroupManagementAdmin />, roles: [USER_ROLES.ADMIN] },
    { path: '/marketplace/admin', element: <AdminMarketplace />, roles: [USER_ROLES.ADMIN] },
    {
        path: '/connect/view-as/:buyerUserId',
        element: <VenueShell venueId={VenueId.Connect} />,
        roles: [USER_ROLES.ADMIN],
        children: [{ path: '', element: <ViewAsBuyerPage /> }],
    },
    { path: '/marketplace/admin/engagement/:engagementId', element: <ConnectMarketplaceDashboard />, roles: [USER_ROLES.ADMIN] },
    { path: '/marketplace/admin/templates/:id', element: <TimelineEditor type="template" />, roles: [USER_ROLES.ADMIN] },
    { path: '/marketplace/admin/engagements/:id', element: <TimelineEditor type="engagement" />, roles: [USER_ROLES.ADMIN] },
    { path: '/marketplace/admin/templates/:id/preview', element: <MarketplaceTemplatePreview type="template" />, roles: [USER_ROLES.ADMIN] },
    { path: '/landscapemap/admin', element: <LandscapeMapDemoPage />, roles: [USER_ROLES.ADMIN] },
    { path: '/maintenance-tool', element: <MaintenanceTool />, roles: [USER_ROLES.ADMIN] },
    { path: '/maintenance-tool/timeline-simulator', element: <TimelineSimulator />, roles: [USER_ROLES.ADMIN] },
    { path: '/maintenance-tool/client-errors', element: <ClientErrorReports />, roles: [USER_ROLES.ADMIN] },
    { path: '/maintenance-tool/quartz-monitor', element: <QuartzMonitor />, roles: [USER_ROLES.ADMIN] },
    { path: '/practice-list', element: <PracticeList />, withAppShell: true, roles: [USER_ROLES.ADMIN] },
    { path: '/data-and-document-templates-dashboard', element: <DataAndDocumentTemplatesDashboard />, withAppShell: true, roles: [USER_ROLES.ADMIN] },
    { path: '/data-and-document-requests-dashboard', element: <DataAndDocumentRequestsDashboard />, withAppShell: true, roles: [USER_ROLES.ADMIN] },
    { path: '/project-editor', element: <ProjectEditorHost />, withAppShell: true, roles: [USER_ROLES.ADMIN] },
];
